import { getCurrentRoute } from "@/router";
import { useAppStore } from "@/stores";

/**
 * Creates a context object for handlers that need access to store properties.
 * This replaces the previous pattern of passing `this.$store.getters` to handlers.
 *
 * `route` carries the current route so the backend can honour the
 * `?feature=nats-piadmin` query override (see createRpcBackend).
 *
 * @returns {Object} Context object with company, session, natsConnection, natsScheme, user and
 * route properties
 */
export function createHandlerContext() {
  const store = useAppStore();
  return {
    session: store.session,
    // Live getters, not snapshots. The NATS connection is established
    // asynchronously (see natsConnect) and the lazy NATS backend reads company +
    // user.id when it first builds; reading all three live lets the backend adopt
    // the connection as soon as it lands (and after a reconnect) with the current
    // tenant/user, instead of pinning handlers built pre-auth/pre-connect to WAMP
    // or the wrong identity.
    get company() {
      return store.company;
    },
    get natsConnection() {
      return store.natsconnection;
    },
    get natsScheme() {
      return store.natsScheme;
    },
    get user() {
      return store.user;
    },
    // Live getter, not a snapshot: the backend evaluates the `?feature=nats-piadmin`
    // override per call. Snapshotting here left handlers built before the route
    // resolved the query (session list, editSession guard, alm) stuck on WAMP.
    get route() {
      return getCurrentRoute();
    },
  };
}
